import { Component } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { v4 as uuid } from "uuid"
import classNames from "classnames"
import { get, filter } from "lodash"
import moment from "moment"
import resizable from "hocs/resizable"
import tooltipContainer from "hocs/tooltip-container"
import Tooltip from "components/charts/tooltip"
import { DATE } from "helpers/shapes"
import { LINE, HIGHLIGHTER } from "../shapes"
import ChartDefs from "./components/chart-defs"
import Axis from "./axis"
import Dots from "./dots"
import { getData, getLines, getXAxis, getYAxis, getScaleX, getScaleY, getFindByOffset, getHighlighter } from "./helper"
import "./style.scss"

const AXIS = PropTypes.shape({
  fn: PropTypes.func.isRequired,
  transform: PropTypes.string.isRequired,
  baseline: PropTypes.shape({
    x1: PropTypes.number.isRequired,
    x2: PropTypes.number.isRequired,
    y1: PropTypes.number.isRequired,
    y2: PropTypes.number.isRequired,
  }).isRequired,
})

class CompareChart extends Component {
  static propTypes = {
    lines: PropTypes.arrayOf(LINE).isRequired,
    data: PropTypes.array.isRequired,
    highlighter: HIGHLIGHTER,
    startDate: DATE,
    endDate: DATE,
    width: PropTypes.number,
    resizableRef: PropTypes.func.isRequired,
    xAxis: AXIS.isRequired,
    yAxis: AXIS.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    })).isRequired,
    tooltip: PropTypes.element.isRequired,
    scaleX: PropTypes.func.isRequired,
    scaleY: PropTypes.func.isRequired,
    findByOffset: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
    unsubscribe: PropTypes.func.isRequired,
    setDate: PropTypes.func.isRequired,
    metricSelector: PropTypes.element.isRequired,
    focusedChartLine: PropTypes.string,
    focusChartLine: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    isOneDayGraph: PropTypes.bool.isRequired,
  }

  constructor(props) {
    super(props)
    const chartId = uuid()
    this.pathGradientId = `path-gradient-${chartId}`
    this.axisGradientId = `axis-gradient-${chartId}`
  }

  findDots = offset => {
    const { scaleX, scaleY, items, findByOffset } = this.props

    const datum = findByOffset(offset)
    if (datum) {
      const dots = filter(items, item => !item.disabled)
        .map((item) => {
          const value = get(datum, item.id)
          if (!Number.isFinite(value)) {
            return null
          }
          const y = scaleY(value)
          if (!Number.isFinite(y)) {
            return null
          }
          return { id: item.id, color: item.color, y }
        })
        .filter(Boolean)

      return {
        x: scaleX(datum.date),
        dots,
      }
    }
  }

  setDate = offset => {
    const { setDate, findByOffset } = this.props
    const { date } = findByOffset(offset)
    setDate(moment(date).format("YYYY-MM-DD"))
  }

  handleGraphClick = ev => {
    const box = ev.currentTarget.getBoundingClientRect()
    const offsetX = ev.pageX - box.left
    this.setDate(offsetX)
  }

  computeAxisStyle = () => {
    const { highlighter, width } = this.props
    const axisStyle = {}

    if (highlighter && width) {
      axisStyle.stroke = `url(#${this.axisGradientId})`
    }

    return axisStyle
  }

  computePathStyle = (id, initialStyle) => {
    const { highlighter, width } = this.props
    const pathStyle = {}

    if (highlighter && width) {
      pathStyle.stroke = `url(#${id})`
    } else {
      pathStyle.stroke = initialStyle
    }

    return pathStyle
  }

  render() {
    const {
      resizableRef,
      lines,
      xAxis,
      yAxis,
      subscribe,
      unsubscribe,
      highlighter,
      tooltip,
      width,
      focusedChartLine,
      focusChartLine,
      blurChartLine,
      findByOffset,
      data,
    } = this.props

    return <div className="chart-container">
      <svg ref={resizableRef} onClick={this.handleGraphClick} className="compare-chart">
        <ChartDefs lines={lines} axisId={this.axisGradientId} pathsId={this.pathGradientId} offset={highlighter && highlighter.x1} chartWidth={width} />
        <Axis {...xAxis} />
        <Axis {...yAxis} />
        <line {...xAxis.baseline} className="baseline" style={this.computeAxisStyle()} />
        <line {...yAxis.baseline} className="baseline" />
        {lines.map((line, index) =>
          <path
            key={line.id}
            className={classNames(
              "path focus-enabled",
              {
                visible: focusedChartLine && focusedChartLine === line.id,
                hidden: focusedChartLine && focusedChartLine !== line.id,
                "single-dot": data && data.length === 1,
              },
            )}
            {...line}
            style={this.computePathStyle(`${this.pathGradientId}-${index}`, line.style.stroke)}
          />,
        )}
        {highlighter && <line className="highlighter" {...highlighter} />}
        {lines.length > 0 && <Dots
          {...{
            subscribe,
            unsubscribe,
            findDots: this.findDots,
            focus: focusChartLine,
            blur: blurChartLine,
            focused: focusedChartLine,
          }}
        />}
      </svg>
      <Tooltip {...{ subscribe, unsubscribe, findByOffset, position: "right" }}>
        {tooltip}
      </Tooltip>
    </div>
  }
}

const mapStateToProps = (state, props) => ({
  data: getData(props),
  lines: getLines(props),
  xAxis: getXAxis(props),
  yAxis: getYAxis(props),
  scaleX: getScaleX(props),
  scaleY: getScaleY(props),
  findByOffset: getFindByOffset(props),
  highlighter: getHighlighter(props),
})

export default tooltipContainer(resizable(connect(mapStateToProps)(CompareChart)))
